/**
 * Knowledge Hub types for the Otto Portal.
 *
 * These types model the CRP-backed KnowledgeHub ARM resource
 * (sub-resource of AutomationProject).
 */

/** The type/source of a knowledge hub. */
export type KnowledgeHubType = 'file' | 'foundryiq' | 'ai-search' | 'workiq';

/** Provisioning status of a knowledge hub (container lifecycle, not artifact indexing). */
export type KnowledgeHubStatus = 'ready' | 'provisioning' | 'error' | 'pending';

/**
 * Normalize API status values (including spacing/underscore variants)
 * to internal portal status codes.
 */
export function normalizeKnowledgeHubStatus(status: unknown): KnowledgeHubStatus {
  const normalized = typeof status === 'string'
    ? status.trim().toLowerCase().replace(/[\s_-]+/g, '')
    : '';

  if (normalized === 'ready' || normalized === 'indexed' || normalized === 'succeeded') return 'ready';
  if (normalized === 'indexing' || normalized === 'provisioning' || normalized === 'running') return 'provisioning';
  if (normalized === 'pending' || normalized === 'notstarted' || normalized === 'created') return 'pending';
  if (normalized === 'error' || normalized === 'failed') return 'error';
  return 'pending';
}

/** Artifact-level status returned by the CRP dataplane API. */
export type KnowledgeArtifactStatus = 'InProgress' | 'Completed' | 'Failed' | 'Deleting';

/** A knowledge hub as returned by the ARM API. */
export interface KnowledgeHub {
  /** ARM resource ID. */
  id: string;
  /** Hub name (unique within the automation project). */
  name: string;
  /** Hub type (file, foundryiq, ai-search, workiq). */
  type: KnowledgeHubType;
  properties: KnowledgeHubProperties;
  /**
   * Artifacts in this hub, returned inline when listing hubs.
   * Avoids a separate API call per hub to fetch artifacts.
   */
  artifacts?: KnowledgeArtifact[];
}

/** Properties bag for a KnowledgeHub ARM resource. */
export interface KnowledgeHubProperties {
  /** Human-readable description. */
  description?: string;
  /** The automation project name this hub belongs to. */
  projectName?: string;
  /** Current provisioning status of the hub container. */
  status: KnowledgeHubStatus;
  /** ISO 8601 timestamp of last modification. */
  lastModified: string;
  /** ISO 8601 timestamp of creation. */
  createdAt?: string;
  /** Source type (mirrors top-level type for ARM consistency). */
  sourceType: KnowledgeHubType;
  /** Number of artifacts/documents in this hub. */
  artifactCount?: number;
  /** FoundryIQ-specific configuration. */
  foundryIQConfig?: FoundryIQConfig;
  /** AI Search-specific configuration. */
  aiSearchConfig?: AISearchConfig;
  /** WorkIQ-specific configuration. */
  workIQConfig?: WorkIQConfig;
}

/** Configuration for a FoundryIQ-connected knowledge hub. */
export interface FoundryIQConfig {
  endpoint: string;
  knowledgeBaseName: string;
}

/** Configuration for an AI Search-connected knowledge hub. */
export interface AISearchConfig {
  endpoint: string;
  indexName: string;
  apiKey?: string;
}

/** Configuration for a WorkIQ-connected knowledge hub. */
export interface WorkIQConfig {
  tenantId: string;
  artifactType: string;
}

/** Request payload for creating a file-based knowledge hub. */
export interface CreateFileHubRequest {
  hubName: string;
  description?: string;
  projectName?: string;
}

/** Request payload for creating a FoundryIQ knowledge hub. */
export interface CreateFoundryIQHubRequest {
  hubName: string;
  description?: string;
  projectName?: string;
  config: FoundryIQConfig;
}

/** Request payload for creating an AI Search knowledge hub. */
export interface CreateAISearchHubRequest {
  hubName: string;
  description?: string;
  projectName?: string;
  config: AISearchConfig;
}

/** Request payload for uploading artifacts to a knowledge hub. */
export interface UploadArtifactRequest {
  hubName: string;
  files: File[];
}

/** Properties bag for a KnowledgeArtifact ARM resource. */
export interface KnowledgeArtifactProperties {
  /** Human-readable description. */
  description?: string;
  /** ARM resource ID of the parent knowledge hub. */
  knowledgeHubId?: string;
  /** MIME content type. */
  contentType?: string;
  /** Identity of the user who uploaded the artifact. */
  uploadedBy?: string;
  /** Provisioning status of this artifact (CRP dataplane values). */
  uploadStatus: KnowledgeArtifactStatus;
  /** ISO 8601 timestamp of when the artifact was created/uploaded. */
  createdAt: string;
}

/** A knowledge artifact (document/file) within a knowledge hub. */
export interface KnowledgeArtifact {
  /** ARM resource ID. */
  id: string;
  /** Artifact name (typically the file name). */
  name: string;
  properties: KnowledgeArtifactProperties;
}
